import React, { useState } from "react";
import { Loader } from "lucide-react";
import axios from "axios";
import useUserRole from "../../app/auth/useUserRole";
import { getToken } from "../../app/auth/authConfig";

const NotificationSubscriberRegistration = () => {
  const [formData, setFormData] = useState({ email: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const currentUserRole = useUserRole();

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email.trim()) {
      showToast("Please enter an email address", "error");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showToast("Please enter a valid email address", "error");
      return;
    }

    try {
      setIsLoading(true);
      const token = await getToken();
      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/notifications/subscribers`;

      await axios.post(baseURL, { email: formData.email }, {
        headers: { "oidc-token": token },
      });

      showToast(`✅ Successfully subscribed ${formData.email}`, "success");
      setFormData({ email: "" });
    } catch (error) {
      console.error("Subscription error", error);
      const errMsg = error.response?.data?.error || "Failed to subscribe. Please try again.";
      showToast(errMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: "20px 0", maxWidth: "600px" }}>
      {currentUserRole === null && (
        <div>
          <style>{`
            @keyframes shimmer {
              0% { background-position: -400px 0; }
              100% { background-position: 400px 0; }
            }
            .sk-box {
              background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
              background-size: 800px 100%;
              animation: shimmer 1.4s infinite;
              border-radius: 6px;
            }
          `}</style>
          <div className="sk-box" style={{ height: 16, width: "40%", marginBottom: 24 }} />
          <div className="sk-box" style={{ height: 14, width: "25%", marginBottom: 10 }} />
          <div className="sk-box" style={{ height: 48, width: "100%", marginBottom: 20 }} />
          <div className="sk-box" style={{ height: 44, width: 140 }} />
        </div>
      )}

      {currentUserRole !== null && currentUserRole !== "Admin" && (
        <div
          style={{
            background: "#fee2e2",
            color: "#991b1b",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "24px",
            border: "1px solid #fca5a5",
          }}
        >
          <strong>Access Denied:</strong> Only admins can register subscribers.
        </div>
      )}
      <h3
        style={{
          marginBottom: "24px",
          fontSize: "18px",
          fontWeight: "600",
          color: "#333",
          display: currentUserRole === "Admin" ? "block" : "none",
        }}
      >
        📧 Add New Subscriber
      </h3>

      <form onSubmit={handleSubmit} style={{ display: currentUserRole === "Admin" ? "block" : "none" }}>
        <div style={{ marginBottom: "20px" }}>
          <label
            htmlFor="email"
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
              color: "#333",
              fontSize: "14px",
            }}
          >
            Email Address
          </label>
          <input
            id="email"
            type="email"
            name="email"
            placeholder="subscriber@example.com"
            value={formData.email}
            onChange={handleChange}
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "12px 14px",
              borderRadius: "6px",
              border: "1px solid #ddd",
              backgroundColor: isLoading ? "#f0f0f0" : "white",
              cursor: isLoading ? "not-allowed" : "auto",
              fontSize: "14px",
              boxSizing: "border-box",
              transition: "all 0.3s ease",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#bb1ccc";
              e.target.style.boxShadow = "0 0 0 3px rgba(187,28,204,0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#ddd";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          style={{
            background: isLoading
              ? "#ccc"
              : "linear-gradient(135deg, #bb1ccc 0%, #d946ef 100%)",
            color: "white",
            border: "none",
            padding: "12px 24px",
            borderRadius: "6px",
            cursor: isLoading ? "not-allowed" : "pointer",
            fontSize: "14px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.3s ease",
            opacity: isLoading ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 4px 12px rgba(187,28,204,0.3)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          {isLoading ? (
            <>
              <Loader className="animate-spin" width={16} />
              Subscribing...
            </>
          ) : (
            <>+ Subscribe</>
          )}
        </button>
      </form>

      {toast && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            padding: "16px 20px",
            borderRadius: "6px",
            background: toast.type === "success" ? "#10b981" : "#ef4444",
            color: "white",
            fontSize: "14px",
            fontWeight: "500",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            animation: "slideIn 0.3s ease",
            zIndex: 9999,
          }}
        >
          {toast.message}
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default NotificationSubscriberRegistration;
